// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable react/prop-types */
import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useJsApiLoader, Autocomplete } from '@react-google-maps/api';
import { setNestedObjectValues } from 'formik';
import Input from './bootstrap/forms/Input';
import FormGroup from './bootstrap/forms/FormGroup';
import Select from './bootstrap/forms/Select';
import { usStatesForSelect, GOOGLE_AUTOCOMPLETE_AND_MAPS_LIBRARIES } from '../utils/constants';
import { generateGoogleMapsAddress, getFullAddress } from '../utils/util_funcs';
import { formsAreValidFunc } from '../utils/validationFuncs';
import Collapse from './bootstrap/Collapse';

const GooglePlaceAddress = ({
	googleMapsApiKey,
	formik,
	lockedFields,
	fieldNames,
	scrollContainerId,
	inputLabelString,
	inputDescriptionString,
	forceCollapsed,
	forceExpanded,
	customOnBlurFunc,
	customAfterAddressSelectedFunc,
}) => {
	return googleMapsApiKey ? (
		<GooglePlaceAddressInternal
			formik={formik}
			apiKey={googleMapsApiKey}
			lockedFields={lockedFields}
			fieldNames={fieldNames}
			scrollContainerId={scrollContainerId}
			inputLabelString={inputLabelString}
			inputDescriptionString={inputDescriptionString}
			forceCollapsed={forceCollapsed}
			forceExpanded={forceExpanded}
			customOnBlurFunc={customOnBlurFunc}
			customAfterAddressSelectedFunc={customAfterAddressSelectedFunc}
		/>
	) : (
		<>
			<h5 className=' ekho-placeholder-wave h5 pb-1'>
				<span className='ekho-placeholder col-4' />
			</h5>
			<h1 className=' ekho-placeholder-wave h1'>
				<span className='ekho-placeholder col-12' />
			</h1>
		</>
	);
};

const GooglePlaceAddressInternal = ({
	formik,
	apiKey,
	lockedFields,
	fieldNames,
	scrollContainerId,
	inputLabelString,
	inputDescriptionString,
	forceCollapsed,
	forceExpanded,
	customOnBlurFunc,
	customAfterAddressSelectedFunc,
}) => {
	const getNestedValue = (path, obj) => {
		if (typeof path !== 'string') {
			return null; // Return null if path is not a string
		}

		return path.split('.').reduce((prev, curr) => {
			return prev ? prev[curr] : null;
		}, obj);
	};

	function shouldExpand() {
		// if first field error AND at least one nested field has error
		// then don't expand
		// if first field has no error and contains at least one comma AND at least one nested field has error
		// then expand
		// if first field has error AND no nested field has error
		// then don't expand
		// if first field has no error AND no nested field has error
		// then don't expand
		const nestedFields = [
			fieldNames.addressLineTwo,
			fieldNames.city,
			fieldNames.zip,
			fieldNames.county,
		];

		const outerField = fieldNames.addressLineOne;

		const outerFieldValue = getNestedValue(outerField, formik.values);
		const outerFieldLength = outerFieldValue?.length || 0;
		const isValidationComplete = !formik.isValidating && formik.validateOnBlur;

		// don't do anything if the outer field is empty or has no commas
		if (outerFieldLength < 10 || !isValidationComplete) {
			return false;
		}

		const nestedFieldsHaveErrors = nestedFields.some((fieldName) => {
			const hasError = getNestedValue(fieldName, formik.errors);
			return hasError;
		});

		const outerFieldHasError = getNestedValue(outerField, formik.errors);

		return !outerFieldHasError && nestedFieldsHaveErrors;
	}

	const initialHasErrors = shouldExpand();

	let startExpanded = forceExpanded;
	// if we are not forcing the component to remain collapsed
	if (!forceCollapsed) {
		// if the addressLineOne field is already populated then start expanded
		if (initialHasErrors || getNestedValue(fieldNames.addressLineOne, formik.values)) {
			startExpanded = true;
		}
	}
	const autocompleteRef = useRef();
	const addressInputRef = useRef();
	const [isExpanded, setIsExpanded] = useState(startExpanded);
	const { isLoaded } = useJsApiLoader({
		id: 'google-map-script',
		googleMapsApiKey: apiKey,
		libraries: GOOGLE_AUTOCOMPLETE_AND_MAPS_LIBRARIES,
	});

	const addressLineOneValue = getNestedValue(fieldNames.addressLineOne, formik.values);
	const addressLineTwoErrors = getNestedValue(fieldNames.addressLineTwo, formik.errors);
	const cityErrors = getNestedValue(fieldNames.city, formik.errors);
	const zipErrors = getNestedValue(fieldNames.zip, formik.errors);
	const countyErrors = getNestedValue(fieldNames.county, formik.errors);

	useEffect(() => {
		// Only expand if it should and is not already expanded
		if (
			(!isExpanded && forceExpanded) ||
			((addressLineTwoErrors || cityErrors || zipErrors || countyErrors) && shouldExpand())
		) {
			setIsExpanded(true);
			// Set all error fields as touched once upon expansion
			formik.setTouched(setNestedObjectValues(formik.errors, true));
		}
		// only listen for errors on particular fields
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		addressLineOneValue,
		addressLineTwoErrors,
		cityErrors,
		zipErrors,
		countyErrors,
		isExpanded,
		forceExpanded,
	]);

	// Split the field name by dots to determine the nesting structure
	const setNestedFieldValue = (formikElement, fieldName, value) => {
		formikElement.setFieldValue(fieldName, value);
	};

	async function handleAddressSelect(place) {
		const addressObj = getFullAddress(place);
		const addressString = generateGoogleMapsAddress(addressObj);
		// expand fields after selection to allow for manual editing
		setIsExpanded(true);
		const addressLineOne = addressObj.address1 || '';
		setNestedFieldValue(formik, fieldNames.addressString, addressString);
		setNestedFieldValue(formik, fieldNames.addressLineOne, addressLineOne);
		setNestedFieldValue(formik, fieldNames.addressLineTwo, addressObj.apartmentNumber || '');
		setNestedFieldValue(
			formik,
			fieldNames.city,
			addressObj.locality || addressObj.sublocality || '',
		);
		setNestedFieldValue(formik, fieldNames.state, addressObj.adminArea1Short || '');
		setNestedFieldValue(
			formik,
			fieldNames.zip,
			addressObj.postalCodeExtension
				? `${addressObj.postalCode}-${addressObj.postalCodeExtension}`
				: addressObj.postalCode || '',
		);
		setNestedFieldValue(formik, fieldNames.county, addressObj.adminArea2Long || '');
		setNestedFieldValue(formik, fieldNames.lat, addressObj.latitude || '');
		setNestedFieldValue(formik, fieldNames.lng, addressObj.longitude || '');
		setTimeout(async () => {
			await formsAreValidFunc([formik]);
			if (customAfterAddressSelectedFunc) {
				customAfterAddressSelectedFunc();
			}
		}, 300);
	}

	async function handlePlaceChanged() {
		const place = autocompleteRef.current.getPlace();
		if (place?.address_components) {
			await handleAddressSelect(place);
		}
	}

	function onAutoFillStart() {
		setIsExpanded(true);
	}

	function onAutoFillCancel() {
		// for now do nothing
	}

	function onRefAvailable() {
		const handleAnimationStart = (e) => {
			switch (e.animationName) {
				case 'onAutoFillStart':
					onAutoFillStart();
					break;
				case 'onAutoFillCancel':
					onAutoFillCancel();
					break;
				default:
				// do nothing
			}
		};

		const currentAddressRef = addressInputRef.current;

		if (currentAddressRef) {
			currentAddressRef.addEventListener('animationstart', handleAnimationStart);
			currentAddressRef.addEventListener('transitionstart', handleAnimationStart);
		}

		// Cleanup function to remove the event listener
		return () => {
			if (currentAddressRef) {
				currentAddressRef.removeEventListener('animationstart', handleAnimationStart);
				currentAddressRef.removeEventListener('transitionstart', handleAnimationStart);
			}
		};
	}

	useEffect(() => {
		const handleScroll = () => {
			const autocompleteDropdown = document.querySelector('.pac-container');
			if (autocompleteDropdown && addressInputRef.current) {
				const inputRect = addressInputRef.current.getBoundingClientRect();
				const newTop = inputRect.top + inputRect.height + window.scrollY;
				autocompleteDropdown.style.top = `${newTop}px`;
			}
		};

		// Use document.getElementById to find the element with ID SCROLL_CONTAINER_ID
		const scrollContainer = scrollContainerId
			? document.querySelector(`.${scrollContainerId}`)
			: null;
		if (scrollContainer) {
			scrollContainer.addEventListener('scroll', handleScroll);
		}

		return () => {
			if (scrollContainer) {
				scrollContainer.removeEventListener('scroll', handleScroll);
			}
		};
	}, [addressInputRef, scrollContainerId]);

	return (
		<>
			{isLoaded && (
				<div className='col-12'>
					<Autocomplete
						onLoad={(autocomplete) => {
							autocompleteRef.current = autocomplete;
						}}
						onPlaceChanged={() => handlePlaceChanged()}
						restrictions={{ country: ['us'] }}
						fields={['address_component', 'geometry']}
						types={['address']}>
						<FormGroup
							id='addressLineOne'
							label={
								// eslint-disable-next-line no-nested-ternary
								inputLabelString
									? isExpanded
										? `${inputLabelString} line 1`
										: inputLabelString
									: 'Street address'
							}
							formText={inputDescriptionString || null}>
							<Input
								id='addressLineOne'
								name={fieldNames.addressLineOne}
								size='lg'
								placeholder=''
								type='text'
								required
								autoComplete='street-address'
								onChange={formik.handleChange}
								onBlur={(e) => {
									if (customOnBlurFunc) {
										customOnBlurFunc(e, formik);
									} else {
										formik.handleBlur(e);
									}
								}}
								value={getNestedValue(fieldNames.addressLineOne, formik.values)}
								isValid={formik.isValid}
								isTouched={getNestedValue(
									fieldNames.addressLineOne,
									formik.touched,
								)}
								invalidFeedback={getNestedValue(
									fieldNames.addressLineOne,
									formik.errors,
								)}
								ref={(node) => {
									if (node !== addressInputRef.current) {
										addressInputRef.current = node;
										onRefAvailable();
									}
								}}
							/>
						</FormGroup>
					</Autocomplete>
				</div>
			)}
			{isLoaded && (
				<Collapse
					className='row col-12'
					isOpen={isExpanded}
					style={{ marginTop: '19.5px', paddingRight: 0 }}>
					<div className='col-12' style={{ paddingRight: 0 }}>
						{isLoaded && (
							<FormGroup
								id='addressLineTwo'
								label={
									inputLabelString
										? `${inputLabelString} line 2`
										: 'Apt, Suite, Unit, Company'
								}>
								<Input
									id='addressLineTwo'
									name={fieldNames.addressLineTwo}
									size='lg'
									placeholder=''
									autoComplete='apartment'
									onChange={formik.handleChange}
									onBlur={(e) => {
										if (customOnBlurFunc) {
											customOnBlurFunc(e, formik);
										} else {
											formik.handleBlur(e);
										}
									}}
									value={getNestedValue(fieldNames.addressLineTwo, formik.values)}
									isValid={formik.isValid}
									isTouched={getNestedValue(
										fieldNames.addressLineTwo,
										formik.touched,
									)}
									invalidFeedback={getNestedValue(
										fieldNames.addressLineTwo,
										formik.errors,
									)}
								/>
							</FormGroup>
						)}
						{!isLoaded && (
							<h1 className=' ekho-placeholder-wave h1'>
								<span className='ekho-placeholder col-12 col-sm-6' />
							</h1>
						)}
					</div>
					<div
						className='col-12 col-sm-6'
						style={{ marginTop: '19.5px', paddingRight: 0 }}>
						{isLoaded && (
							<FormGroup id='city' label='City'>
								<Input
									id='city'
									name={fieldNames.city}
									size='lg'
									placeholder=''
									required
									autoComplete='address-level2'
									onChange={formik.handleChange}
									onBlur={(e) => {
										if (customOnBlurFunc) {
											customOnBlurFunc(e, formik);
										} else {
											formik.handleBlur(e);
										}
									}}
									value={getNestedValue(fieldNames.city, formik.values)}
									isValid={formik.isValid}
									isTouched={getNestedValue(fieldNames.city, formik.touched)}
									invalidFeedback={getNestedValue(fieldNames.city, formik.errors)}
								/>
							</FormGroup>
						)}
						{!isLoaded && (
							<h1 className=' ekho-placeholder-wave h1'>
								<span className='ekho-placeholder col-12 col-sm-6' />
							</h1>
						)}
					</div>
					<div
						className='col-12 col-sm-6'
						style={{ marginTop: '19.5px', paddingRight: 0 }}>
						{isLoaded && (
							<FormGroup id='county' label='County'>
								<Input
									id='county'
									name={fieldNames.county}
									size='lg'
									placeholder=''
									required
									autoComplete='address-level3'
									onChange={formik.handleChange}
									onBlur={(e) => {
										if (customOnBlurFunc) {
											customOnBlurFunc(e, formik);
										} else {
											formik.handleBlur(e);
										}
									}}
									value={getNestedValue(fieldNames.county, formik.values)}
									isValid={formik.isValid}
									isTouched={getNestedValue(fieldNames.county, formik.touched)}
									invalidFeedback={getNestedValue(
										fieldNames.county,
										formik.errors,
									)}
								/>
							</FormGroup>
						)}
						{!isLoaded && (
							<h1 className=' ekho-placeholder-wave h1'>
								<span className='ekho-placeholder col-12 col-sm-6' />
							</h1>
						)}
					</div>
					<div
						className='col-12 col-sm-6'
						style={{ marginTop: '19.5px', paddingRight: 0 }}>
						{isLoaded && (
							<FormGroup
								id='state'
								label='State'
								formText={
									lockedFields &&
									lockedFields.has(fieldNames.state) &&
									formik.initialValues[fieldNames.state]
										? 'State is fixed to your initial selection'
										: null
								}>
								<Select
									id='state'
									name={fieldNames.state}
									ariaLabel='State'
									placeholder='Select'
									disabled={
										lockedFields.has(fieldNames.state) &&
										formik.initialValues.state
									}
									size='lg'
									required
									autoComplete='address-level1' // state
									list={usStatesForSelect}
									onChange={formik.handleChange}
									onBlur={(e) => {
										if (customOnBlurFunc) {
											customOnBlurFunc(e, formik);
										} else {
											formik.handleBlur(e);
										}
									}}
									value={getNestedValue(fieldNames.state, formik.values)}
									isValid={formik.isValid}
									isTouched={getNestedValue(fieldNames.state, formik.touched)}
									invalidFeedback={getNestedValue(
										fieldNames.state,
										formik.errors,
									)}
								/>
							</FormGroup>
						)}
						{!isLoaded && (
							<h1 className=' ekho-placeholder-wave h1'>
								<span className='ekho-placeholder col-12 col-sm-6' />
							</h1>
						)}
					</div>
					<div
						className='col-12 col-sm-6'
						style={{ marginTop: '19.5px', paddingRight: 0 }}>
						{isLoaded && (
							<FormGroup id='zip' label='Zip'>
								<Input
									id='zip'
									name={fieldNames.zip}
									size='lg'
									placeholder=''
									required
									autoComplete='postal-code'
									onChange={formik.handleChange}
									onBlur={(e) => {
										if (customOnBlurFunc) {
											customOnBlurFunc(e, formik);
										} else {
											formik.handleBlur(e);
										}
									}}
									value={getNestedValue(fieldNames.zip, formik.values)}
									isValid={formik.isValid}
									isTouched={getNestedValue(fieldNames.zip, formik.touched)}
									invalidFeedback={getNestedValue(fieldNames.zip, formik.errors)}
								/>
							</FormGroup>
						)}
						{!isLoaded && (
							<h1 className=' ekho-placeholder-wave h1'>
								<span className='ekho-placeholder col-12 col-sm-6' />
							</h1>
						)}
					</div>
				</Collapse>
			)}
			{!isLoaded && !isExpanded && (
				<h1 className=' ekho-placeholder-wave h1'>
					<span className=' ekho-placeholder col-12' />
				</h1>
			)}
		</>
	);
};

// Custom validator for a prop of type Set
// eslint-disable-next-line consistent-return
const setType = (props, propName, componentName) => {
	const prop = props[propName];
	if (!(prop instanceof Set)) {
		return new Error(
			`Invalid prop \`${propName}\` of type \`${typeof prop}\` supplied to \`${componentName}\`, expected \`Set\`.`,
		);
	}
};

GooglePlaceAddressInternal.propTypes = {
	formik: PropTypes.shape({
		values: PropTypes.shape({
			addressString: PropTypes.string,
			addressLineOne: PropTypes.string,
			addressLineTwo: PropTypes.string,
			city: PropTypes.string,
			state: PropTypes.string,
			zip: PropTypes.string,
			county: PropTypes.string,
		}).isRequired,
		// eslint-disable-next-line react/forbid-prop-types
		initialValues: PropTypes.object.isRequired,
		setValues: PropTypes.func.isRequired,
		handleChange: PropTypes.func.isRequired,
		handleBlur: PropTypes.func.isRequired,
		touched: PropTypes.shape({
			// mirror the shape of your form fields
			addressString: PropTypes.bool,
			addressLineOne: PropTypes.bool,
			addressLineTwo: PropTypes.bool,
			city: PropTypes.bool,
			state: PropTypes.bool,
			zip: PropTypes.bool,
			county: PropTypes.bool,
		}).isRequired,
		isValid: PropTypes.bool.isRequired,
		errors: PropTypes.shape({
			// mirror the shape of your form fields
			addressString: PropTypes.string,
			addressLineOne: PropTypes.string,
			addressLineTwo: PropTypes.string,
			city: PropTypes.string,
			state: PropTypes.string,
			zip: PropTypes.string,
			county: PropTypes.string,
		}).isRequired,
	}).isRequired,
	// eslint-disable-next-line react/forbid-prop-types
	lockedFields: setType,
	fieldNames: PropTypes.shape({
		addressString: PropTypes.string,
		addressLineOne: PropTypes.string,
		addressLineTwo: PropTypes.string,
		city: PropTypes.string,
		state: PropTypes.string,
		zip: PropTypes.string,
		county: PropTypes.string,
	}).isRequired,
	apiKey: PropTypes.string,
	// eslint-disable-next-line react/forbid-prop-types
	scrollContainerId: PropTypes.string,
	inputDescriptionString: PropTypes.string,
	inputLabelString: PropTypes.string,
	forceCollapsed: PropTypes.bool.isRequired,
	forceExpanded: PropTypes.bool.isRequired,
	customOnBlurFunc: PropTypes.func,
	customAfterAddressSelectedFunc: PropTypes.func,
};

GooglePlaceAddressInternal.defaultProps = {
	lockedFields: new Set(),
	apiKey: '',
	scrollContainerId: null,
	inputLabelString: null,
	inputDescriptionString: null,
	customOnBlurFunc: null,
	customAfterAddressSelectedFunc: null,
};

GooglePlaceAddress.propTypes = {
	googleMapsApiKey: PropTypes.string.isRequired,
	formik: PropTypes.shape({
		values: PropTypes.shape({
			addressString: PropTypes.string,
			addressLineOne: PropTypes.string,
			addressLineTwo: PropTypes.string,
			city: PropTypes.string,
			state: PropTypes.string,
			zip: PropTypes.string,
			county: PropTypes.string,
		}).isRequired,
		// eslint-disable-next-line react/forbid-prop-types
		initialValues: PropTypes.object.isRequired,
		setValues: PropTypes.func.isRequired,
		handleChange: PropTypes.func.isRequired,
		handleBlur: PropTypes.func.isRequired,
		touched: PropTypes.shape({
			// mirror the shape of your form fields
			addressString: PropTypes.bool,
			addressLineOne: PropTypes.bool,
			addressLineTwo: PropTypes.bool,
			city: PropTypes.bool,
			state: PropTypes.bool,
			zip: PropTypes.bool,
			county: PropTypes.bool,
		}).isRequired,
		isValid: PropTypes.bool.isRequired,
		errors: PropTypes.shape({
			// mirror the shape of your form fields
			addressString: PropTypes.string,
			addressLineOne: PropTypes.string,
			addressLineTwo: PropTypes.string,
			city: PropTypes.string,
			state: PropTypes.string,
			zip: PropTypes.string,
			county: PropTypes.string,
		}).isRequired,
	}).isRequired,
	// eslint-disable-next-line react/forbid-prop-types
	lockedFields: setType,
	fieldNames: PropTypes.shape({
		addressString: PropTypes.string,
		addressLineOne: PropTypes.string,
		addressLineTwo: PropTypes.string,
		city: PropTypes.string,
		state: PropTypes.string,
		zip: PropTypes.string,
		county: PropTypes.string,
	}),
	// eslint-disable-next-line react/forbid-prop-types
	scrollContainerId: PropTypes.string,
	inputLabelString: PropTypes.string,
	inputDescriptionString: PropTypes.string,
	// eslint-disable-next-line react/no-unused-prop-types
	isNested: PropTypes.bool,
	// eslint-disable-next-line react/no-unused-prop-types
	nestedPrefix: PropTypes.string,
	forceCollapsed: PropTypes.bool,
	forceExpanded: PropTypes.bool,
	customOnBlurFunc: PropTypes.func,
	customAfterAddressSelectedFunc: PropTypes.func,
};

GooglePlaceAddress.defaultProps = {
	lockedFields: new Set(),
	fieldNames: {
		addressString: 'addressString',
		addressLineOne: 'addressLineOne',
		addressLineTwo: 'addressLineTwo',
		city: 'city',
		state: 'state',
		zip: 'zip',
		county: 'county',
		lat: 'lat',
		lng: 'lng',
	},
	scrollContainerId: null,
	inputLabelString: null,
	inputDescriptionString: null,
	isNested: false,
	nestedPrefix: '',
	forceCollapsed: false,
	forceExpanded: false,
	customOnBlurFunc: null,
	customAfterAddressSelectedFunc: null,
};

export default GooglePlaceAddress;
